// @ts-nocheck
// Caixa de perguntas (admin) — criação + gestão.
import React, { useState, useEffect } from 'react';
import { Icon } from '../icons';
import { toast } from '../toast';
import { useTerm } from './components';
import { api } from '../../services/apiV2';

interface OpenQuestion {
  id: string;
  text: string;
  receivedAgo: string;
  similarCount: number;
  reaction: number;
}
interface AnsweredQuestion {
  id: string;
  text: string;
  answer: string;
  answeredAgo: string;
  likes: number;
}

const MOCK_QUESTIONS_OPEN: OpenQuestion[] = [
  { id: 'q1', text: 'Quando Bonhoeffer fala em "graça barata", ele inclui a forma como nossas igrejas trabalham hoje? Pareceu duro demais.', receivedAgo: 'há 4h', similarCount: 3, reaction: 8 },
  { id: 'q2', text: 'Não entendi a diferença entre seguir Cristo e imitar Cristo no capítulo do discipulado. Alguém pode explicar com outras palavras?', receivedAgo: 'há 1d', similarCount: 0, reaction: 5 },
  { id: 'q3', text: 'A passagem da página 187 me incomodou. É proposital? Como vocês lidaram?', receivedAgo: 'há 1d', similarCount: 0, reaction: 2 },
  { id: 'q4', text: 'Existe uma boa biografia complementar pra ler em paralelo?', receivedAgo: 'há 2d', similarCount: 1, reaction: 1 },
];

const MOCK_QUESTIONS_ANSWERED: AnsweredQuestion[] = [
  { id: 'q-a-1', text: 'Por que ele resiste tanto a uma fé só interior?', answer: 'Porque pra Bonhoeffer fé sem corpo é abstração. A obediência concreta — a vida no mundo — é o teste de que a graça foi recebida.', answeredAgo: 'há 3d', likes: 22 },
  { id: 'q-a-2', text: 'O que ele quer dizer com "discipulado custoso"?', answer: 'É o oposto da graça barata: a graça que custa tudo, porque chama o discípulo. Custa porque chama; é graça porque chama.', answeredAgo: 'há 5d', likes: 18 },
];

const PROMPT_SUGGESTIONS = [
  'Dúvidas sobre o que foi lido até a página 200?',
  'Que parte deste capítulo te incomodou?',
  'Algo que ficou confuso e quer que eu retome no encontro?',
  'Pergunta livre — pergunte o que quiser sobre o livro.',
];

const DURATION_OPTS = [
  { value: '1d', label: '1 dia' },
  { value: '3d', label: '3 dias' },
  { value: '7d', label: '1 semana' },
  { value: 'open', label: 'Sem fechar' },
];

const TabBtn: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> = ({ active, onClick, children }) => (
  <button onClick={onClick} aria-selected={active} style={{
    padding: '12px 4px',
    background: 'transparent',
    position: 'relative',
    display: 'inline-flex', alignItems: 'center', gap: 8,
    fontSize: 13.5, fontWeight: 500,
    color: active ? 'var(--ember)' : 'var(--ink-3)',
    borderBottom: '2px solid ' + (active ? 'var(--ember)' : 'transparent'),
    marginBottom: -1, marginRight: 16,
  }}>
    {children}
  </button>
);

const pillStyle = (active: boolean): React.CSSProperties => ({
  fontFamily: 'Geist Mono, monospace',
  fontSize: 10,
  padding: '2px 8px', borderRadius: 999,
  background: active ? 'var(--ember-soft)' : 'var(--paper-3)',
  color: active ? 'var(--ember)' : 'var(--ink-3)',
  fontWeight: 600, letterSpacing: '0.04em',
});

const OpenQuestionCard: React.FC<{
  q: OpenQuestion; isAnswering: boolean; draft: string;
  setDraft: (v: string) => void;
  posting?: boolean;
  onStartAnswer: () => void; onCancel: () => void; onSubmit: () => void;
}> = ({ q, isAnswering, draft, setDraft, posting = false, onStartAnswer, onCancel, onSubmit }) => (
  <div style={{
    padding: 16, borderRadius: 18,
    background: 'var(--paper-2)', border: '1px solid var(--line)',
    transition: 'all 200ms',
    boxShadow: isAnswering ? '0 14px 28px -14px rgba(199,83,26,0.30)' : 'none',
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
      <div style={{
        width: 26, height: 26, borderRadius: '50%',
        background: 'var(--paper-3)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'var(--ink-3)',
      }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="9" r="3.5"/>
          <path d="M5 20c1.5-3 4-4.5 7-4.5s5.5 1.5 7 4.5"/>
        </svg>
      </div>
      <div className="mono" style={{
        fontSize: 10, letterSpacing: '0.10em', textTransform: 'uppercase',
        color: 'var(--ink-3)', fontWeight: 500,
      }}>
        anônimo · {q.receivedAgo}
      </div>
      {q.similarCount > 0 && (
        <div className="mono" style={{
          marginLeft: 'auto',
          fontSize: 9.5, padding: '3px 8px', borderRadius: 999,
          background: 'var(--ember-soft)', color: 'var(--ember)',
          letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 600,
        }}>
          +{q.similarCount} parecida{q.similarCount > 1 ? 's' : ''}
        </div>
      )}
    </div>
    <div className="serif" style={{
      fontSize: 17, lineHeight: 1.35, color: 'var(--ink)', textWrap: 'pretty',
    }}>"{q.text}"</div>
    <div style={{
      marginTop: 12, display: 'flex', alignItems: 'center', gap: 6,
      fontSize: 11.5, color: 'var(--ink-3)',
    }}>
      <span style={{ fontSize: 14, lineHeight: 1 }}>🙋</span>
      <span><strong style={{ color: 'var(--ink-2)' }}>{q.reaction}</strong> têm a mesma dúvida</span>
    </div>
    {!isAnswering ? (
      <div style={{ marginTop: 14, display: 'flex', gap: 8 }}>
        <button onClick={onStartAnswer} style={{
          flex: 1, padding: '11px 14px', borderRadius: 12,
          background: 'var(--ember)', color: 'var(--paper)',
          fontSize: 13, fontWeight: 500,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        }}>
          <Icon name="pen" size={13} color="var(--paper)"/> Responder
        </button>
        <button style={{
          padding: '11px 14px', borderRadius: 12,
          background: 'transparent',
          border: '1px solid var(--line-strong)',
          color: 'var(--ink-3)', fontSize: 12.5, fontWeight: 500,
        }}>Adiar</button>
      </div>
    ) : (
      <div style={{ marginTop: 14 }}>
        <div className="mono" style={{
          fontSize: 9, letterSpacing: '0.18em', textTransform: 'uppercase',
          color: 'var(--ember)', fontWeight: 600, marginBottom: 6,
        }}>
          sua resposta · vai virar 'resposta do líder' no mural
        </div>
        <textarea value={draft} onChange={e => setDraft(e.target.value)}
          placeholder="Responda como se estivesse falando com o grupo todo..." autoFocus
          style={{
            width: '100%', minHeight: 100,
            padding: 12, borderRadius: 12,
            background: 'var(--paper)',
            border: '1px solid var(--ember)',
            fontSize: 14, lineHeight: 1.5, resize: 'none',
            outline: 'none', color: 'var(--ink)', fontFamily: 'inherit',
          }}/>
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <button onClick={onSubmit} disabled={!draft.trim() || posting} style={{
            flex: 1, padding: '11px 14px', borderRadius: 12,
            background: draft.trim() && !posting ? 'var(--ink)' : 'var(--paper-3)',
            color: draft.trim() && !posting ? 'var(--paper)' : 'var(--ink-4)',
            fontSize: 13, fontWeight: 500,
            cursor: draft.trim() && !posting ? 'pointer' : 'not-allowed',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}>
            {posting ? 'Publicando…' : 'Publicar resposta'}
            <Icon name="arrow-right" size={13} color={draft.trim() ? 'var(--paper)' : 'var(--ink-4)'}/>
          </button>
          <button onClick={onCancel} style={{
            padding: '11px 14px', borderRadius: 12,
            background: 'transparent', color: 'var(--ink-3)', fontSize: 12.5,
          }}>Cancelar</button>
        </div>
      </div>
    )}
  </div>
);

const AnsweredQuestionCard: React.FC<{ q: AnsweredQuestion }> = ({ q }) => (
  <div style={{
    padding: 0, borderRadius: 18,
    background: 'var(--paper-2)', border: '1px solid var(--line)',
    overflow: 'hidden',
  }}>
    <div style={{ padding: '14px 16px 0' }}>
      <div className="mono" style={{
        fontSize: 9, letterSpacing: '0.16em', textTransform: 'uppercase',
        color: 'var(--ink-3)', fontWeight: 600,
      }}>alguém perguntou</div>
      <div className="serif" style={{
        fontSize: 15, lineHeight: 1.3, fontStyle: 'italic',
        color: 'var(--ink-2)', marginTop: 4, textWrap: 'pretty',
      }}>"{q.text}"</div>
    </div>
    <div style={{
      margin: '12px 16px 14px', padding: '12px 14px', borderRadius: 12,
      background: 'color-mix(in oklab, var(--ember) 8%, var(--paper))',
      borderLeft: '3px solid var(--ember)',
    }}>
      <div className="mono" style={{
        fontSize: 9, letterSpacing: '0.16em', textTransform: 'uppercase',
        color: 'var(--ember)', fontWeight: 600,
      }}>✨ você respondeu</div>
      <div style={{
        fontSize: 13.5, lineHeight: 1.5,
        color: 'var(--ink)', marginTop: 6, textWrap: 'pretty',
      }}>{q.answer}</div>
      <div style={{
        marginTop: 10, display: 'flex', alignItems: 'center', gap: 12,
        fontSize: 11, color: 'var(--ink-3)',
      }}>
        <span>{q.answeredAgo || 'agora'}</span>
        <span>·</span>
        <span><strong style={{ color: 'var(--ink-2)' }}>{q.likes ?? 0}</strong> agradeceram</span>
      </div>
    </div>
  </div>
);

const CreateQuestionBoxScreen: React.FC<{
  term: string; T: any; onBack: () => void;
  onOpen: (data: { prompt: string; duration: string; anonymous: boolean }) => void;
  creating?: boolean;
}> = ({ term, T, onBack, onOpen, creating = false }) => {
  const [prompt, setPrompt] = useState('');
  const [duration, setDuration] = useState('3d');
  const [anonymous, setAnonymous] = useState(true);
  const canOpen = prompt.trim().length > 4 && !creating;
  return (
    <div className="scroll" style={{ paddingBottom: 130 }}>
      <div style={{ padding: '54px 20px 0' }}>
        <button onClick={onBack} style={{
          width: 36, height: 36, borderRadius: '50%',
          background: 'var(--paper-2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          border: '1px solid var(--line)',
        }}>
          <Icon name="chevron-left" size={16}/>
        </button>
        <div className="mono" style={{
          fontSize: 9.5, letterSpacing: '0.18em', textTransform: 'uppercase',
          color: 'var(--ember)', marginTop: 18, fontWeight: 600,
        }}>modo líder · nova caixa</div>
        <div className="serif" style={{
          fontSize: 28, lineHeight: 1.05, marginTop: 6, letterSpacing: '-0.01em',
        }}>
          Abra a caixa de<br/>
          <span style={{ fontStyle: 'italic', color: 'var(--ember)' }}>perguntas</span>.
        </div>
        <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 10, maxWidth: 320, lineHeight: 1.45 }}>
          Crie um espaço onde os membros podem enviar dúvidas {anonymous ? 'anônimas ' : ''}que só você responde como líder.
        </div>
      </div>

      <div style={{ padding: '24px 20px 0' }}>
        <label className="mono" style={{
          display: 'block',
          fontSize: 9.5, letterSpacing: '0.16em', textTransform: 'uppercase',
          color: 'var(--ink-3)', fontWeight: 600, marginBottom: 8,
        }}>seu prompt para o {term}</label>
        <textarea value={prompt} onChange={e => setPrompt(e.target.value.slice(0, 140))}
          placeholder="Ex: Dúvidas sobre o capítulo 3?" autoFocus
          style={{
            width: '100%', minHeight: 88,
            padding: 14, borderRadius: 14,
            background: 'var(--paper-2)',
            border: '1px solid var(--line)',
            fontFamily: 'inherit',
            fontSize: 15, lineHeight: 1.4, resize: 'none',
            outline: 'none', color: 'var(--ink)',
          }}/>
        <div className="mono" style={{
          marginTop: 4, fontSize: 9.5, letterSpacing: '0.06em',
          color: 'var(--ink-4)', textAlign: 'right',
        }}>{prompt.length}/140</div>

        <div className="mono" style={{
          marginTop: 12,
          fontSize: 9, letterSpacing: '0.16em', textTransform: 'uppercase',
          color: 'var(--ink-4)', fontWeight: 600,
        }}>ideias rápidas</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
          {PROMPT_SUGGESTIONS.map(s => (
            <button key={s} onClick={() => setPrompt(s)} style={{
              padding: '7px 11px', borderRadius: 999,
              background: 'transparent',
              border: '1px solid var(--line)',
              fontSize: 11.5, color: 'var(--ink-2)',
              lineHeight: 1.3, textAlign: 'left',
            }}>
              {s.length > 38 ? s.slice(0, 36) + '…' : s}
            </button>
          ))}
        </div>
      </div>

      <div style={{ padding: '24px 20px 0' }}>
        <label className="mono" style={{
          display: 'block',
          fontSize: 9.5, letterSpacing: '0.16em', textTransform: 'uppercase',
          color: 'var(--ink-3)', fontWeight: 600, marginBottom: 8,
        }}>fica aberta por</label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
          {DURATION_OPTS.map(d => {
            const sel = duration === d.value;
            return (
              <button key={d.value} onClick={() => setDuration(d.value)} style={{
                padding: '12px 8px', borderRadius: 12,
                background: sel ? 'var(--ember-soft)' : 'var(--paper-2)',
                border: '1px solid ' + (sel ? 'var(--ember)' : 'var(--line)'),
                color: sel ? 'var(--ember)' : 'var(--ink-2)',
                fontSize: 12, fontWeight: sel ? 600 : 500,
                textAlign: 'center',
              }}>{d.label}</button>
            );
          })}
        </div>
      </div>

      <div style={{ padding: '20px 20px 0' }}>
        <button onClick={() => setAnonymous(a => !a)} style={{
          width: '100%', padding: 14, borderRadius: 14,
          background: 'var(--paper-2)',
          border: '1px solid var(--line)',
          display: 'flex', alignItems: 'center', gap: 12,
          textAlign: 'left',
        }}>
          <span style={{
            width: 36, height: 36, borderRadius: 10,
            background: anonymous ? 'var(--ember-soft)' : 'var(--paper-3)',
            color: anonymous ? 'var(--ember)' : 'var(--ink-3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="9" r="3.5"/>
              <path d="M5 20c1.5-3 4-4.5 7-4.5s5.5 1.5 7 4.5"/>
            </svg>
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 500 }}>Perguntas anônimas</div>
            <div className="mono" style={{
              marginTop: 2, fontSize: 9.5, letterSpacing: '0.06em', color: 'var(--ink-3)',
            }}>
              {anonymous ? 'membros enviam sem se identificar' : 'membros enviam com nome'}
            </div>
          </div>
          <div style={{
            width: 38, height: 22, borderRadius: 999,
            background: anonymous ? 'var(--ember)' : 'var(--paper-3)',
            position: 'relative', flexShrink: 0,
            transition: 'background 200ms',
          }}>
            <div style={{
              position: 'absolute', top: 2, left: anonymous ? 18 : 2,
              width: 18, height: 18, borderRadius: '50%',
              background: 'var(--paper)',
              transition: 'left 200ms',
            }}/>
          </div>
        </button>
      </div>

      <div style={{
        position: 'absolute', left: 0, right: 0, bottom: 0,
        padding: '12px 20px 26px',
        background: 'linear-gradient(to top, var(--paper) 60%, rgba(0,0,0,0))',
        display: 'flex', flexDirection: 'column', gap: 8,
      }}>
        <button onClick={() => canOpen && onOpen({ prompt: prompt.trim(), duration, anonymous })}
          disabled={!canOpen} style={{
          width: '100%', padding: 15, borderRadius: 16,
          background: canOpen ? 'var(--ember)' : 'var(--paper-3)',
          color: canOpen ? 'var(--paper)' : 'var(--ink-4)',
          fontSize: 15, fontWeight: 500,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          cursor: canOpen ? 'pointer' : 'not-allowed',
          boxShadow: canOpen ? '0 14px 28px -10px rgba(199,83,26,0.55)' : 'none',
        }}>
          {creating ? 'Abrindo…' : 'Abrir caixa'} <Icon name="arrow-right" size={15} color={canOpen ? 'var(--paper)' : 'var(--ink-4)'}/>
        </button>
        <div className="mono" style={{
          fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase',
          color: 'var(--ink-4)', textAlign: 'center',
        }}>aparece como card no estudo do livro</div>
      </div>
    </div>
  );
};

// Selector: lista TODAS as Caixas de Perguntas do clube. Cada card mostra
// prompt, contador de perguntas (abertas/respondidas), tempo desde criação,
// duração configurada. Click no card → leva pra tela admin daquela caixa.
// Botão "+ Nova Caixa" no topo abre a tela de criação.
const QboxSelectorScreen: React.FC<{
  T: any;
  session: any;
  clubId: string;
  onBack: () => void;
  onCreateNew: () => void;
  onPick: (postId: string) => void;
}> = ({ T, session, clubId, onBack, onCreateNew, onPick }) => {
  const [qboxes, setQboxes] = useState<any[] | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!session || !clubId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const r = await api.listQuestionBoxes(session, clubId);
        if (!cancelled) setQboxes(r.qboxes || []);
      } catch (e) {
        console.error('[qboxes/list]', e);
        if (!cancelled) setQboxes([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [session, clubId]);

  const totalOpen = (qboxes || []).reduce((acc, qb) => acc + (qb.openQuestions || 0), 0);

  return (
    <div className="scroll" style={{ paddingBottom: 40 }}>
      <div style={{ padding: '54px 20px 0' }}>
        <button onClick={onBack} style={{
          width: 36, height: 36, borderRadius: '50%',
          background: 'var(--paper-2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          border: '1px solid var(--line)',
        }}>
          <Icon name="chevron-left" size={16}/>
        </button>
        <div className="mono" style={{
          fontSize: 9.5, letterSpacing: '0.18em', textTransform: 'uppercase',
          color: 'var(--ember)', marginTop: 18, fontWeight: 600,
        }}>modo líder · caixas de perguntas</div>
        <div className="serif" style={{
          fontSize: 28, lineHeight: 1.05, marginTop: 6, letterSpacing: '-0.01em',
        }}>
          {qboxes && qboxes.length > 0 ? (
            <>Suas <span style={{ fontStyle: 'italic', color: 'var(--ember)' }}>caixas</span> ativas</>
          ) : (
            <>Abra uma <span style={{ fontStyle: 'italic', color: 'var(--ember)' }}>caixa</span></>
          )}
        </div>
        {totalOpen > 0 && (
          <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 8, lineHeight: 1.4 }}>
            <strong style={{ color: 'var(--ember)' }}>{totalOpen}</strong> {totalOpen === 1 ? 'pergunta aguardando' : 'perguntas aguardando'} sua resposta.
          </div>
        )}
      </div>

      <div style={{ padding: '20px 20px 0', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* CTA: nova caixa (sempre visível) */}
        <button onClick={onCreateNew} style={{
          padding: '14px 16px', borderRadius: 14,
          background: 'linear-gradient(135deg, var(--ember), #E07A3B)',
          border: 'none', color: 'var(--paper)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          fontSize: 14, fontWeight: 500, cursor: 'pointer',
          boxShadow: '0 12px 24px -10px rgba(199,83,26,0.55)',
        }}>
          <Icon name="plus" size={14} color="var(--paper)" stroke={2.4}/>
          Abrir nova Caixa
        </button>

        {loading && (qboxes === null || qboxes.length === 0) && (
          <div style={{ padding: '28px 16px', textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>
            Carregando caixas…
          </div>
        )}

        {!loading && qboxes !== null && qboxes.length === 0 && (
          <div style={{
            padding: 24, textAlign: 'center',
            border: '1px dashed var(--line-strong)', borderRadius: 14,
            color: 'var(--ink-3)', fontSize: 13,
          }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>📥</div>
            <div className="serif" style={{ fontSize: 16, fontStyle: 'italic', marginBottom: 4 }}>
              Nenhuma caixa aberta
            </div>
            <div style={{ fontSize: 12.5, lineHeight: 1.45 }}>
              Abra uma caixa pra receber dúvidas anônimas dos membros sobre o livro.
            </div>
          </div>
        )}

        {/* Cards das caixas */}
        {(qboxes || []).map(qb => (
          <button
            key={qb.postId}
            onClick={() => onPick(qb.postId)}
            style={{
              width: '100%', textAlign: 'left',
              padding: 14, borderRadius: 14,
              background: qb.openQuestions > 0
                ? 'color-mix(in oklab, var(--ember) 6%, var(--paper-2))'
                : 'var(--paper-2)',
              border: '1px solid ' + (qb.openQuestions > 0 ? 'var(--ember)' : 'var(--line)'),
              cursor: 'pointer', transition: 'all 160ms',
              display: 'flex', flexDirection: 'column', gap: 10,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <span className="mono" style={{
                fontSize: 9, letterSpacing: '0.16em', textTransform: 'uppercase',
                color: 'var(--ember)', fontWeight: 600,
              }}>📥 caixa · {qb.timeAgo}</span>
              {qb.openQuestions > 0 && (
                <span className="mono" style={{
                  fontSize: 9, padding: '3px 8px', borderRadius: 999,
                  background: 'var(--ember)', color: 'var(--paper)',
                  letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 600,
                }}>
                  {qb.openQuestions} {qb.openQuestions === 1 ? 'aguardando' : 'aguardando'}
                </span>
              )}
            </div>
            <div className="serif" style={{
              fontSize: 17, lineHeight: 1.3, fontStyle: 'italic',
              color: 'var(--ink)', textWrap: 'pretty',
            }}>"{qb.prompt}"</div>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12,
              fontSize: 11.5, color: 'var(--ink-3)',
              borderTop: '1px solid var(--line)', paddingTop: 10,
            }}>
              <span><strong style={{ color: 'var(--ink-2)' }}>{qb.totalQuestions}</strong> recebidas</span>
              <span style={{ color: 'var(--line-strong)' }}>·</span>
              <span><strong style={{ color: qb.openQuestions > 0 ? 'var(--ember)' : 'var(--ink-3)' }}>{qb.openQuestions}</strong> abertas</span>
              <span style={{ color: 'var(--line-strong)' }}>·</span>
              <span><strong style={{ color: 'var(--sage)' }}>{qb.answeredQuestions}</strong> respondidas</span>
              {qb.closesIn && (
                <span style={{ marginLeft: 'auto', color: 'var(--ink-4)', fontFamily: 'Geist Mono, monospace' }}>
                  fecha em {qb.closesIn}
                </span>
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export const ClubQuestionBoxAdminScreen: React.FC<{
  term?: 'clube' | 'grupo';
  onBack: () => void;
  onPublish?: (data: { qid: string; text: string }) => void;
  initialPhase?: 'create' | 'active';
  // Cria a Caixa de Perguntas no backend. Resolve quando o post é criado e o
  // feed do Estudo já foi recarregado pelo App.tsx. Ao terminar, a tela navega
  // de volta pro Estudo via onBack().
  onCreateBox?: (data: { prompt: string; duration: string; anonymous: boolean }) => Promise<void>;
  // Se postId presente + session: tela ativa carrega perguntas reais daquela
  // caixa específica. Sem postId mas com session+clubId: tela ativa lista TODAS
  // as Caixas do clube e o admin escolhe qual gerenciar (selector).
  // Sem nada: fallback ao mock antigo.
  session?: any;
  clubId?: string;
  postId?: string;
  // Quando admin clica numa caixa do selector, navega pra a versão com postId
  onSelectQbox?: (postId: string) => void;
  // Após o admin responder uma pergunta, o App pode recarregar o feed do Estudo
  // pra mostrar o leader-answer recém-criado.
  onAnswered?: () => void;
}> = ({
  term = 'clube', onBack, onPublish, initialPhase = 'active', onCreateBox,
  session, clubId, postId, onSelectQbox, onAnswered,
}) => {
  const T = useTerm(term);
  const [phase, setPhase] = useState<'create' | 'active'>(initialPhase);
  const [activePrompt, setActivePrompt] = useState('Dúvidas sobre o que foi lido até a página 200?');
  const [activeDuration, setActiveDuration] = useState('3d');
  const [tab, setTab] = useState<'open' | 'answered'>('open');
  const [answering, setAnswering] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [answeredIds, setAnsweredIds] = useState<string[]>([]);
  const [justOpened, setJustOpened] = useState(false);
  const [creating, setCreating] = useState(false);

  // Estado real (vindo do backend) — quando postId+session presentes
  const [realQuestions, setRealQuestions] = useState<any[] | null>(null);
  const [realLoading, setRealLoading] = useState(false);
  const [realPrompt, setRealPrompt] = useState<string | null>(null);
  const [realClosesIn, setRealClosesIn] = useState<string | null>(null);
  const [posting, setPosting] = useState(false);
  const useReal = !!(session && clubId && postId);
  const useSelector = !!(session && clubId && !postId && phase === 'active');

  // Carrega perguntas reais ao montar / sempre que muda postId
  useEffect(() => {
    if (!useReal || phase !== 'active') return;
    let cancelled = false;
    setRealLoading(true);
    (async () => {
      try {
        const r = await api.listQuestionBoxQuestions(session, clubId!, postId!);
        if (!cancelled) {
          setRealQuestions(r.questions || []);
          setRealPrompt(r.prompt || null);
          setRealClosesIn(r.closesIn || null);
        }
      } catch (e) {
        console.error('[qbox/list]', e);
        if (!cancelled) setRealQuestions([]);
      } finally {
        if (!cancelled) setRealLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [useReal, session, clubId, postId, phase]);

  // Selector: tela "ativa" SEM postId mostra lista de caixas do clube
  if (useSelector) {
    return (
      <QboxSelectorScreen
        T={T}
        session={session}
        clubId={clubId!}
        onBack={onBack}
        onCreateNew={() => setPhase('create')}
        onPick={(pid: string) => onSelectQbox?.(pid)}
      />
    );
  }

  if (phase === 'create') {
    return (
      <CreateQuestionBoxScreen
        term={term} T={T} onBack={onBack}
        creating={creating}
        onOpen={async ({ prompt, duration, anonymous }) => {
          // Atualiza UI local
          setActivePrompt(prompt);
          setActiveDuration(duration);
          // Chama backend (se handler injetado via App.tsx)
          if (onCreateBox) {
            setCreating(true);
            try {
              await onCreateBox({ prompt, duration, anonymous });
              // Volta pro Estudo (App vai re-renderizar o feed com o novo post)
              onBack();
            } catch (e: any) {
              toast.error(e?.message || 'Erro ao abrir caixa de perguntas');
            } finally {
              setCreating(false);
            }
            return;
          }
          // Fallback: comportamento mock antigo (sem backend)
          setJustOpened(true);
          setPhase('active');
        }}/>
    );
  }

  // Lista visível: se temos dados reais do backend, usa-os; senão, mock antigo.
  let open: any[] = [];
  let answered: any[] = [];
  if (useReal && realQuestions !== null) {
    // Mapeia formato do backend pro formato esperado pela UI desse componente
    const realOpen = realQuestions.filter(q => !q.answered).map(q => ({
      id: q.id,
      text: q.text,
      receivedAgo: q.timeAgo,
      askerName: q.askerName, // só admin vê
    }));
    const realAnswered = realQuestions.filter(q => q.answered).map(q => ({
      id: q.id,
      text: q.text,
      answer: q.answerText || '',
      answeredAgo: q.answeredAt ? '' : 'agora',
      likes: 0,
    }));
    open = realOpen;
    answered = realAnswered;
  } else {
    open = justOpened ? [] : MOCK_QUESTIONS_OPEN.filter(q => !answeredIds.includes(q.id));
    const justAnswered = justOpened ? [] : MOCK_QUESTIONS_OPEN.filter(q => answeredIds.includes(q.id));
    answered = justOpened ? [] : [
      ...justAnswered.map(q => ({ id: q.id, text: q.text, answer: 'Resposta publicada agora.', answeredAgo: 'agora', likes: 0 })),
      ...MOCK_QUESTIONS_ANSWERED,
    ];
  }

  // Atualiza prompt visível com o real do backend (se disponível)
  const displayPrompt = realPrompt ?? activePrompt;
  const displayDuration = realClosesIn ?? activeDuration;

  const submitAnswer = async () => {
    if (!draft.trim() || !answering) return;
    if (useReal) {
      setPosting(true);
      try {
        await api.answerQuestionBoxQuestion(session, clubId!, postId!, answering, draft.trim());
        // Recarrega a lista local
        const r = await api.listQuestionBoxQuestions(session, clubId!, postId!);
        setRealQuestions(r.questions || []);
        setAnswering(null);
        setDraft('');
        // Notifica App pra recarregar o feed do Estudo (leader-answer apareceu)
        onAnswered?.();
      } catch (e: any) {
        toast.error(e?.message || 'Erro ao responder pergunta');
      } finally {
        setPosting(false);
      }
      return;
    }
    // Fallback mock
    setAnsweredIds([...answeredIds, answering]);
    onPublish && onPublish({ qid: answering, text: draft });
    setAnswering(null);
    setDraft('');
  };

  const list: any[] = tab === 'open' ? open : answered;

  return (
    <div className="scroll" style={{ paddingBottom: 40 }}>
      <div style={{ padding: '54px 20px 0' }}>
        <button onClick={onBack} style={{
          width: 36, height: 36, borderRadius: '50%',
          background: 'var(--paper-2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          border: '1px solid var(--line)',
        }}>
          <Icon name="chevron-left" size={16}/>
        </button>
        <div className="mono" style={{
          fontSize: 9.5, letterSpacing: '0.18em',
          textTransform: 'uppercase', color: 'var(--ember)',
          marginTop: 18, fontWeight: 600,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
        }}>
          <span>modo líder · caixa de perguntas</span>
          <button onClick={() => { setJustOpened(false); setPhase('create'); }} className="mono" style={{
            fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase',
            padding: '5px 10px', borderRadius: 999,
            background: 'transparent',
            border: '1px solid var(--line-strong)',
            color: 'var(--ink-3)', fontWeight: 600,
          }}>encerrar · nova</button>
        </div>
        <div className="serif" style={{
          fontSize: 28, lineHeight: 1.05, marginTop: 6, letterSpacing: '-0.01em',
        }}>
          O que estão<br/>
          <span style={{ fontStyle: 'italic', color: 'var(--ember)' }}>perguntando</span>.
        </div>
        <div style={{
          marginTop: 18, padding: '14px 16px', borderRadius: 16,
          background: 'var(--paper-2)', border: '1px solid var(--line)',
        }}>
          <div className="mono" style={{
            fontSize: 9, letterSpacing: '0.18em', textTransform: 'uppercase',
            color: 'var(--ink-3)', fontWeight: 600,
          }}>seu prompt</div>
          <div className="serif" style={{
            fontSize: 16, lineHeight: 1.3, marginTop: 4, fontStyle: 'italic',
            color: 'var(--ink)', textWrap: 'pretty',
          }}>"{displayPrompt}"</div>
          <div style={{
            marginTop: 10, display: 'flex', gap: 14, alignItems: 'center',
            fontSize: 11.5, color: 'var(--ink-3)',
          }}>
            <span><strong style={{ color: 'var(--ink)' }}>{open.length + answered.length}</strong> recebidas</span>
            <span style={{ color: 'var(--line-strong)' }}>·</span>
            <span><strong style={{ color: 'var(--ember)' }}>{open.length}</strong> aguardando</span>
            {displayDuration && (
              <>
                <span style={{ color: 'var(--line-strong)' }}>·</span>
                <span>fecha em <strong style={{ color: 'var(--ink)' }}>{displayDuration}</strong></span>
              </>
            )}
          </div>
        </div>
      </div>

      <div style={{
        marginTop: 20, padding: '0 20px',
        display: 'flex', gap: 4,
        borderBottom: '1px solid var(--line)',
      }}>
        <TabBtn active={tab === 'open'} onClick={() => setTab('open')}>
          Abertas <span style={pillStyle(tab === 'open')}>{open.length}</span>
        </TabBtn>
        <TabBtn active={tab === 'answered'} onClick={() => setTab('answered')}>
          Respondidas <span style={pillStyle(tab === 'answered')}>{answered.length}</span>
        </TabBtn>
      </div>

      <div style={{ padding: '16px 20px 24px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {list.length === 0 && (
          <div style={{ padding: '40px 16px', textAlign: 'center', color: 'var(--ink-3)' }}>
            <div style={{ fontSize: 32, marginBottom: 10 }}>{justOpened ? '📬' : '🌱'}</div>
            <div className="serif" style={{ fontSize: 16, fontStyle: 'italic', textWrap: 'pretty' }}>
              {justOpened
                ? 'Caixa aberta. Aguardando as primeiras perguntas chegarem.'
                : tab === 'open' ? 'Nenhuma pergunta aberta agora.' : 'Você ainda não respondeu nada.'}
            </div>
            {justOpened && (
              <div className="mono" style={{
                marginTop: 14, fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase',
                color: 'var(--ink-4)',
              }}>
                membros do {term} vêem o card no estudo do livro
              </div>
            )}
          </div>
        )}
        {useReal && realLoading && list.length === 0 && (
          <div style={{ padding: '40px 16px', textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>
            Carregando perguntas…
          </div>
        )}
        {list.map(q => tab === 'open'
          ? <OpenQuestionCard key={q.id} q={q}
              isAnswering={answering === q.id} draft={draft} setDraft={setDraft}
              posting={posting && answering === q.id}
              onStartAnswer={() => { setAnswering(q.id); setDraft(''); }}
              onCancel={() => { setAnswering(null); setDraft(''); }}
              onSubmit={submitAnswer}/>
          : <AnsweredQuestionCard key={q.id} q={q}/>
        )}
      </div>
    </div>
  );
};
